import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Search, ChevronRight, CornerDownLeft, Move } from 'lucide-react'
import PinyinMatch from 'pinyin-match'
import type { NodeMoreOpItem } from '../model/types'
import { useCategorizedOpMenu, type OpMenuCategory } from '../model/use-categorized-op-menu'
import { cn } from '@/shared/lib/utils'
import { Popover, PopoverAnchor, PopoverContent } from '@/shared/components/ui/popover'
import menuImage from '@/assets/images/menu_image.svg';
import menuText from '@/assets/images/menu_text.svg';
import menuAudio from '@/assets/images/menu_audio.svg';
import menuVideo from '@/assets/images/menu_video.svg';
import menuGallery from '@/assets/images/menu_image_list.svg';
import menuTextList from '@/assets/images/menu_text_list.svg';
import menuAudioList from '@/assets/images/menu_audio_list.svg';
import menuVideoList from '@/assets/images/menu_video_list.svg';

export function CategorizedOpMenu({
  ops,
  sourceNodeType,
  onSelect,
}: {
  ops: NodeMoreOpItem[]
  sourceNodeType: string
  onSelect?: (item: NodeMoreOpItem) => void
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeCategoryId, setActiveCategoryId] = useState<OpMenuCategory | null>(null)
  const [activeOpIndex, setActiveOpIndex] = useState<number>(0)
  const [isSubMenu, setIsSubMenu] = useState(false)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const clearTimeouts = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
  }

  const handleMouseLeaveWrapper = () => {
    clearTimeouts()
    closeTimeoutRef.current = setTimeout(() => {
      setActiveCategoryId(null)
      setIsSubMenu(false)
    }, 200)
  }

  const handleMouseEnterWrapper = () => {
    clearTimeouts()
  }

  const handleItemMouseEnter = (id: OpMenuCategory) => {
    clearTimeouts()
    if (activeCategoryId && activeCategoryId !== id) {
      openTimeoutRef.current = setTimeout(() => {
        setActiveCategoryId(id)
        setIsSubMenu(false)
      }, 200)
    } else {
      setActiveCategoryId(id)
      setIsSubMenu(false)
    }
  }

  useEffect(() => {
    return () => clearTimeouts()
  }, [])

  useEffect(() => {
    setActiveOpIndex(0)
  }, [searchQuery])

  const categories = useCategorizedOpMenu(ops, sourceNodeType)

  const filteredOps = useMemo(() => {
    if (!searchQuery.trim()) return []
    const query = searchQuery.trim().toLowerCase()
    return ops.filter((op) => matchesOpLabel(op.label, query))
  }, [ops, searchQuery])

  const menuConfig: { id: OpMenuCategory; label: string; icon: string }[] = [
    { id: 'image', label: '图片', icon: menuImage },
    { id: 'text', label: '文本', icon: menuText },
    { id: 'audio', label: '音频', icon: menuAudio },
    { id: 'video', label: '视频', icon: menuVideo },
    { id: 'gallery', label: '图片列表', icon: menuGallery },
    { id: 'text-list', label: '文本列表', icon: menuTextList },
    { id: 'audio-list', label: '音频列表', icon: menuAudioList },
    { id: 'video-list', label: '视频列表', icon: menuVideoList },
  ]

  const activeCategories = menuConfig.filter((config) => categories[config.id].length > 0)

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Search mode
    if (searchQuery.trim()) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveOpIndex(prev => Math.min(prev + 1, filteredOps.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveOpIndex(prev => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const item = filteredOps[activeOpIndex]
        if (item && !item.disabled) {
          onSelect?.(item)
        }
      }
      return
    }

    // Normal mode
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (isSubMenu) {
        const ops = activeCategoryId ? categories[activeCategoryId] : []
        setActiveOpIndex(prev => Math.min(prev + 1, Math.max(0, ops.length - 1)))
      } else {
        if (!activeCategoryId) {
          setActiveCategoryId(activeCategories[0]?.id || null)
        } else {
          const currentIndex = activeCategories.findIndex(c => c.id === activeCategoryId)
          const nextIndex = Math.min(currentIndex + 1, activeCategories.length - 1)
          setActiveCategoryId(activeCategories[nextIndex]?.id || null)
        }
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (isSubMenu) {
        setActiveOpIndex(prev => Math.max(prev - 1, 0))
      } else {
        if (!activeCategoryId) {
          setActiveCategoryId(activeCategories[activeCategories.length - 1]?.id || null)
        } else {
          const currentIndex = activeCategories.findIndex(c => c.id === activeCategoryId)
          const nextIndex = Math.max(currentIndex - 1, 0)
          setActiveCategoryId(activeCategories[nextIndex]?.id || null)
        }
      }
    } else if (e.key === 'ArrowRight') {
      if (!isSubMenu && activeCategoryId && categories[activeCategoryId]?.length > 0) {
        e.preventDefault()
        setIsSubMenu(true)
        setActiveOpIndex(0)
      }
    } else if (e.key === 'ArrowLeft') {
      if (isSubMenu) {
        e.preventDefault()
        setIsSubMenu(false)
      }
    } else if (e.key === 'Enter') {
      if (isSubMenu && activeCategoryId) {
        e.preventDefault()
        const item = categories[activeCategoryId][activeOpIndex]
        if (item && !item.disabled) {
          onSelect?.(item)
        }
      } else if (!isSubMenu && activeCategoryId && categories[activeCategoryId]?.length > 0) {
        e.preventDefault()
        setIsSubMenu(true)
        setActiveOpIndex(0)
      }
    }
  }

  // 当处于搜索模式时
  if (searchQuery.trim()) {
    return (
      <div className="flex flex-col gap-2" onKeyDown={handleKeyDown}>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8B8B8B]" />
          <input
            type="text"
            placeholder="搜索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 w-full rounded-md border border-[#494949] bg-[#1E1E1E] pl-8 pr-3 text-[13px] text-white outline-none placeholder:text-[#6B6B6B] focus:border-[#6B6B6B]"
            autoFocus
          />
        </div>
        <div className="mb-1 px-1 text-[12px] text-[#8B8B8B]">搜索结果：</div>
        <div className="max-h-[235px] w-full overflow-y-auto space-y-1 [&::-webkit-scrollbar]:h-[2px] [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-track-piece]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-[2px] [&::-webkit-scrollbar-thumb]:bg-[#4B4B4B]">
          {filteredOps.length === 0 ? (
            <div className="py-4 text-center text-[13px] text-[#6B6B6B]">暂无匹配项</div>
          ) : (
            filteredOps.map((item, index) => (
              <OpItemRow
                key={item.op}
                item={item}
                highlightQuery={searchQuery}
                isFocused={activeOpIndex === index}
                onMouseEnter={() => setActiveOpIndex(index)}
                onSelect={onSelect}
              />
            ))
          )}
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-[#353535] pt-2 px-1 text-[11px] text-[#8F8F8F]">
          <div className='flex items-center gap-1'>
            <Move className="h-3 w-3" />
            <span className="flex items-center gap-1">选择</span>
          </div>
          <div className='flex items-center gap-1'>
            <CornerDownLeft className="h-3 w-3" />
            <span className="flex items-center gap-1">执行</span>
          </div>
        </div>
      </div>
    )
  }

  // 常规分类级联模式
  return (
    <div className="flex flex-col gap-2" onKeyDown={handleKeyDown}>
      <div className="relative">
        <Search className="absolute left-2.5 right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8B8B8B]" />
        <input
          type="text"
          placeholder="搜索"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-8 w-full rounded-md border border-[#494949] bg-[#1E1E1E] pl-8 pr-3 text-[13px] text-white outline-none placeholder:text-[#6B6B6B] focus:border-[#6B6B6B]"
          autoFocus
        />
      </div>

      <div className="px-1 text-[12px] font-medium text-[#BDBDBD]">进一步生成</div>

      <div
        className="relative w-full space-y-1"
        onMouseLeave={handleMouseLeaveWrapper}
        onMouseEnter={handleMouseEnterWrapper}
      >
        {activeCategories.map((config) => (
          <Popover key={config.id} open={activeCategoryId === config.id}>
            <div className="group">
              <PopoverAnchor asChild>
                <button
                  type="button"
                  onMouseEnter={() => handleItemMouseEnter(config.id)}
                  className={cn(
                    'nodrag flex w-full items-center justify-between rounded-[8px] px-3 py-2.5 text-left text-[14px] transition',
                    activeCategoryId === config.id ? 'bg-[#3A3A3A] text-[#FAFAFA]' : 'text-[#E1E1E1] hover:bg-[#3A3A3A] hover:text-[#FAFAFA]'
                  )}
                >
                  <span className="flex items-center gap-2">
                    <img alt="空图标" className="h-4 w-4" src={config.icon} />
                    <span>{config.label}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 opacity-50" />
                </button>
              </PopoverAnchor>
              <PopoverContent
                side="right"
                align="start"
                sideOffset={8}
                className="w-[160px] p-2 rounded-[12px] border border-[#494949] bg-[#292929] shadow-[0_18px_48px_rgba(0,0,0,0.48)] ring-0"
                onOpenAutoFocus={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => {
                  // 防止点击外部时导致主菜单关闭
                  e.preventDefault()
                }}
                onMouseEnter={handleMouseEnterWrapper}
                onMouseLeave={handleMouseLeaveWrapper}
              >
                <div className="max-h-[300px] overflow-y-auto space-y-1 [&::-webkit-scrollbar]:h-[4px] [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-track-piece]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-[4px] [&::-webkit-scrollbar-thumb]:bg-[#4B4B4B]">
                  {categories[config.id].map((item, index) => (
                    <OpItemRow
                      key={item.op}
                      item={item}
                      isFocused={isSubMenu && activeOpIndex === index}
                      onMouseEnter={() => {
                        setActiveOpIndex(index)
                        setIsSubMenu(true)
                      }}
                      onSelect={onSelect}
                    />
                  ))}
                </div>
              </PopoverContent>
            </div>
          </Popover>
        ))}
        {activeCategories.length === 0 && (
          <div className="py-4 text-center text-[13px] text-[#6B6B6B]">无可用操作</div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-[#353535] pt-2 px-1 text-[11px] text-[#8F8F8F]">
        <div className='flex items-center gap-1'>
          <Move className="h-3 w-3" />
          <span className="flex items-center gap-1">选择</span>
        </div>
        <div className='flex items-center gap-1'>
          <CornerDownLeft className="h-3 w-3" />
          <span className="flex items-center gap-1">执行</span>
        </div>
      </div>
    </div>
  )
}

function matchesOpLabel(label: string, query: string) {
  return Boolean(resolveLabelMatchRange(label, query))
}

function resolveLabelMatchRange(label: string, query: string): [number, number] | null {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return null

  const directMatchStart = label.toLowerCase().indexOf(normalizedQuery)
  if (directMatchStart >= 0) {
    return [directMatchStart, directMatchStart + normalizedQuery.length - 1]
  }

  return PinyinMatch.match(label, normalizedQuery) || null
}

function renderHighlightedLabel(label: string, query?: string) {
  if (!query?.trim()) return label

  const range = resolveLabelMatchRange(label, query)
  if (!range) return label

  const [start, end] = range
  return (
    <>
      {label.slice(0, start)}
      <span className="text-[#FACC15]">{label.slice(start, end + 1)}</span>
      {label.slice(end + 1)}
    </>
  )
}

function OpItemRow({
  item,
  highlightQuery,
  isFocused,
  onMouseEnter,
  onSelect,
}: {
  item: NodeMoreOpItem
  highlightQuery?: string
  isFocused?: boolean
  onMouseEnter?: () => void
  onSelect?: (item: NodeMoreOpItem) => void
}) {
  const ref = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    if (isFocused && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest' })
    }
  }, [isFocused])

  return (
    <button
      ref={ref}
      type="button"
      disabled={item.disabled}
      onMouseEnter={onMouseEnter}
      onPointerDown={(event) => {
        // 防止焦点丢失
        event.preventDefault()
        // 使用 onPointerDown 而不是 onClick 触发选中，防止被外层检测到的 Outside Click 拦截卸载导致 onClick 丢失
        if (item.disabled) return
        onSelect?.(item)
      }}
      onClick={() => { }}
      className={cn(
        'nodrag flex w-full items-center justify-between rounded-[8px] border border-transparent px-2 py-2.5 text-left text-[14px] transition outline-none',
        isFocused ? 'bg-[#3A3A3A] text-[#FAFAFA]' : 'text-[#E1E1E1] hover:bg-[#3A3A3A] hover:text-[#FAFAFA]',
        item.disabled && 'cursor-not-allowed opacity-45'
      )}
    >
      <span className="inline-flex min-w-0 items-center gap-2">
        <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-white/70">
          {item.icon}
        </span>
        <span className="truncate">{renderHighlightedLabel(item.label, highlightQuery)}</span>
      </span>
    </button>
  )
}
